import {assertUnreachable} from '@wandb/weave/common/util/types';

import {View} from '../views/types';
import {generateUserWorkspaceDisplayName} from './namedWorkspaceActionsInternal';
import {PRIMARY_WORKSPACE_DISPLAY_NAME} from './namedWorkspacesActions';
import {findView, viewNameColumnForNamedWorkspace} from './names';
import {
  getIdForUserPersonalNamedWorkspace,
  getNamedWorkspaceId,
  NamedWorkspaceId,
  NW_PRIMARY_ID,
  NW_PUBLISHED_USERNAME,
  WsRelatedId,
} from './types';
import {WorkspaceUrl} from './url';

/**
 * This is called when the user visits the root project URL (eg `wandb.ai/myEntity/myProj`),
 * this method determines if the primary workspace (or a particular user's draft) needs to be created.
 *
 * TODO: we will add support for saving a user's last visited workspace in a particular project
 * and send them there if they
 */
export const determineLoadActionForRootProjectUrl = (
  views: View[],
  viewerUsername: string | undefined,
  relatedId: WsRelatedId
): WorkspaceLoadAction => {
  if (!viewerUsername) {
    // user is not logged in
    const nwId = getNamedWorkspaceId(
      NW_PRIMARY_ID,
      NW_PUBLISHED_USERNAME,
      relatedId
    );
    // if there's no viewer, we will always show the published version of primary
    const view = findView(views, nwId);
    if (view) {
      return {type: 'load', viewCid: view.cid, nwId};
    } else {
      return {type: 'createPrimaryPublished', relatedId};
    }
  }
  const draftNwId = getNamedWorkspaceId(
    NW_PRIMARY_ID,
    viewerUsername,
    relatedId
  );
  const draftView = findView(views, draftNwId);
  if (draftView) {
    return {type: 'load', viewCid: draftView.cid, nwId: draftNwId};
  }
  const publishedNwId = getNamedWorkspaceId(
    NW_PRIMARY_ID,
    NW_PUBLISHED_USERNAME,
    relatedId
  );
  const publishedView = findView(views, publishedNwId);
  if (!publishedView) {
    return {
      type: 'createPrimaryPublishedAndDraft',
      username: viewerUsername,
      relatedId,
    };
  } else {
    return {
      type: 'createPrimaryDraft',
      relatedId,
      username: viewerUsername,
      templateViewCid: publishedView.cid,
    };
  }
};

const NOT_FOUND_ERROR_MESSAGE = "That user's workspace could not be found.";

/**
 * The set of actions we can take when visiting a url.
 *
 * We don't support creating workspaces via visiting a URL, so 'load'
 * is the normal use case, but the primary workspace is special, so we allow creating it.
 *
 * We differentiate between the create scenarios since we already
 * have the list of views, so we can avoid loading the metadatalist for views
 * later by figuring out what needs to be created here.
 */
export type WorkspaceLoadAction =
  | {type: 'load'; viewCid: string; nwId: NamedWorkspaceId}
  | {type: 'createPublished'; nwId: NamedWorkspaceId; displayName: string}
  | {
      type: 'createPrimaryDraft';
      username: string;
      relatedId: WsRelatedId;
      templateViewCid: string;
    }
  | {
      type: 'createPrimaryPublishedAndDraft';
      relatedId: WsRelatedId;
      username: string;
    }
  // this is a special case when there isn't a current viewer, so we aren't creating the user draft
  | {type: 'createPrimaryPublished'; relatedId: WsRelatedId}
  | {
      type: 'error';
      errorCode: 'notFound' | 'noEmpty';
      userMessage?: string;
      debugContext?: any;
    };

/**
 * Figures out what we should do when the workspace first loads from a URL.
 * It notably uses the list of views for the entity/project/run/group/sweep/etc,
 * which means that we know if we need to create things.
 */
export const determineLoadActionForUrl = (
  workspaceUrl: WorkspaceUrl,
  viewerUsername: string | undefined,
  views: View[],
  relatedId: WsRelatedId
): WorkspaceLoadAction => {
  switch (workspaceUrl.type) {
    case 'empty':
      return {
        type: 'error',
        errorCode: 'noEmpty',
        userMessage: NOT_FOUND_ERROR_MESSAGE,
      };
    case 'default':
      return determineLoadActionForPublishedWorkspace(
        getNamedWorkspaceId(NW_PRIMARY_ID, NW_PUBLISHED_USERNAME, relatedId),
        views,
        'allowCreate',
        PRIMARY_WORKSPACE_DISPLAY_NAME
      );

    case 'user':
      return determineLoadActionForPublishedWorkspace(
        getNamedWorkspaceId(
          getIdForUserPersonalNamedWorkspace(workspaceUrl.user),
          NW_PUBLISHED_USERNAME,
          relatedId
        ),
        views,
        // We don't have an explicit reason why we shouldn't allow this, but it seems weird.
        // If users really want this, it shouldn't break anything.
        workspaceUrl.user === viewerUsername ? 'allowCreate' : 'noCreate',
        generateUserWorkspaceDisplayName(workspaceUrl.user, views)
      );

    case 'none':
      // user did not specify a workspace id when visiting the project
      return determineLoadActionForRootProjectUrl(
        views,
        viewerUsername,
        relatedId
      );
    case 'named':
      // user specified a specific nwId, so load that named workspace
      const username = workspaceUrl.username
        ? // if the user explicitly specified a username, use that one
          workspaceUrl.username
        : // user didn't specific a username in the url, so let's load theirs
          // ... and if there isn't actually a current viewer then we'll use the template
          viewerUsername ?? NW_PUBLISHED_USERNAME;

      if (
        workspaceUrl.username !== NW_PUBLISHED_USERNAME &&
        viewerUsername !== workspaceUrl.username &&
        workspaceUrl.username != null
      ) {
        throw new Error("cannot access another user's draft");
      }

      const nwId = getNamedWorkspaceId(workspaceUrl.id, username, relatedId);
      const view = findView(views, nwId);
      if (!view) {
        return {
          type: 'error',
          errorCode: 'notFound',
          userMessage: NOT_FOUND_ERROR_MESSAGE,
          debugContext: {
            views,
            nwId,
            name: viewNameColumnForNamedWorkspace(nwId),
          },
        };
      }

      return {type: 'load', viewCid: view.cid, nwId};
    default:
      assertUnreachable(workspaceUrl);
  }
};

const determineLoadActionForPublishedWorkspace = (
  nwId: NamedWorkspaceId,
  views: View[],
  canCreate: 'allowCreate' | 'noCreate',
  createDisplayName: string | 'noCreate'
): WorkspaceLoadAction => {
  const view = findView(views, nwId);
  if (nwId.username !== NW_PUBLISHED_USERNAME) {
    throw new Error('Can only create published workspace');
  }
  if (view) {
    return {
      type: 'load',
      nwId,
      viewCid: view.cid,
    };
  }
  switch (canCreate) {
    case 'noCreate':
      return {
        type: 'error',
        errorCode: 'notFound',
        userMessage: NOT_FOUND_ERROR_MESSAGE,
      };
    case 'allowCreate':
      if (createDisplayName === 'noCreate') {
        throw new Error('must supply createDisplayName for ' + canCreate);
      }
      return {type: 'createPublished', nwId, displayName: createDisplayName};
    default:
      assertUnreachable(canCreate);
  }
};
